import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog"
import { Users, BookOpen, FileText, CheckSquare, BookPlus, FileUp } from "lucide-react"
import { adminDashboard } from "../../services/api" // Adjust the import path as needed

// Interface pour les statistiques du dashboard
interface DashboardCounts {
  users: {
    total: number
    growthPercentage: number
  }
  formations: {
    total: number
    thisMonth: number
  }
  inscriptions: {
    total: number
    growthPercentage: number
  }
}

// Interface pour les inscriptions en attente
interface PendingInscription {
  id: number
  fullName: string
  email: string
  phoneNumber: string
  formation: string
  demandeDate: string
  status: string
}

// Interface pour les détails utilisateur dans le modal
interface UserDetails {
  id: number
  name: string
  email: string
  phone: string
  course: string
  requestDate: string
  status: string
  approvalDate?: string
  rejectionDate?: string
}

export default function DashboardAdmin() {
  // États pour les données dynamiques
  const [dashboardCounts, setDashboardCounts] = useState<DashboardCounts | null>(null)
  const [pendingInscriptions, setPendingInscriptions] = useState<PendingInscription[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // États pour les filtres et pagination (conservés pour usage futur)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [courseFilter, setCourseFilter] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null)

  // Chargement des données au montage du composant
  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Charger les statistiques et les inscriptions en parallèle
      const [countsResponse, pendingResponse] = await Promise.all([
        adminDashboard.getCounts(),
        adminDashboard.getPendingInscriptions()
      ])

      setDashboardCounts(countsResponse.data)
      setPendingInscriptions(pendingResponse.data.data || [])
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err)
      setError('Erreur lors du chargement des données du tableau de bord')
    } finally {
      setLoading(false)
    }
  }

  const openUserDetails = (inscription: PendingInscription) => {
    setSelectedUser({
      id: inscription.id,
      name: inscription.fullName,
      email: inscription.email,
      phone: inscription.phoneNumber,
      course: inscription.formation,
      requestDate: inscription.demandeDate,
      status: inscription.status
    })
    setIsDetailsModalOpen(true)
  }

  const handleApproveInscription = async (inscriptionId: number) => {
    try {
      await adminDashboard.approveInscription(inscriptionId)
      
      // Recharger les données après approbation
      await loadDashboardData()
      
      // Fermer le modal si l'inscription approuvée était sélectionnée
      if (selectedUser && selectedUser.id === inscriptionId) {
        setIsDetailsModalOpen(false)
      }
    } catch (err) {
      console.error('Erreur lors de l\'approbation:', err)
      setError('Erreur lors de l\'approbation de l\'inscription')
    }
  }

  // Obtenir la classe CSS pour le badge de statut
  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "approuvée": return "bg-green-50 text-green-700 border-green-200"
      case "en attente": return "bg-yellow-50 text-yellow-700 border-yellow-200"
      case "rejetée": return "bg-red-50 text-red-700 border-red-200"
      default: return ""
    }
  }

  // Fonction pour formater la date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR')
  }

  // Fonction pour formater le pourcentage de croissance
  const formatGrowthPercentage = (percentage: number) => {
    const sign = percentage >= 0 ? '+' : ''
    return `${sign}${percentage}%`
  }

  if (loading) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p>Chargement du tableau de bord...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadDashboardData}>Réessayer</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container px-4 py-8 md:px-6 md:py-12">
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Tableau de bord administrateur</h1>
          <p className="text-gray-500 dark:text-gray-400">Bienvenue dans l'espace d'administration d'Elille Academy.</p>
        </div>

        {/* Statistiques générales */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Utilisateurs</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dashboardCounts?.users.total.toLocaleString() || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                {dashboardCounts?.users.growthPercentage !== undefined 
                  ? `${formatGrowthPercentage(dashboardCounts.users.growthPercentage)} par rapport au mois dernier`
                  : 'Aucune donnée de croissance'
                }
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Formations</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dashboardCounts?.formations.total || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                {dashboardCounts?.formations.thisMonth 
                  ? `${dashboardCounts.formations.thisMonth} nouvelles formations ce mois-ci`
                  : 'Aucune nouvelle formation ce mois-ci'
                }
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Inscriptions</CardTitle>
              <CheckSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dashboardCounts?.inscriptions.total.toLocaleString() || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                {dashboardCounts?.inscriptions.growthPercentage !== undefined
                  ? `${formatGrowthPercentage(dashboardCounts.inscriptions.growthPercentage)} par rapport au mois dernier`
                  : 'Aucune donnée de croissance'
                }
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Accès rapides</CardTitle>
              <CardDescription>Accédez rapidement aux fonctionnalités principales</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <a href="/admin/users">
                  <Button variant="outline" className="w-full justify-start">
                    <Users className="mr-2 h-4 w-4" />
                    Gérer les utilisateurs
                  </Button>
                </a>
                <a href="/admin/courses/new">
                  <Button variant="outline" className="w-full justify-start">
                    <BookPlus className="mr-2 h-4 w-4" />
                    Ajouter une formation
                  </Button>
                </a>
                <a href="/admin/inscriptions">
                  <Button variant="outline" className="w-full justify-start">
                    <CheckSquare className="mr-2 h-4 w-4" />
                    Gérer les inscriptions
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Inscriptions en attente */}
          <Card className="md:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle>Inscriptions en attente</CardTitle>
              <CardDescription>
                Demandes d'inscription nécessitant votre approbation ({pendingInscriptions.length})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pendingInscriptions.length > 0 ? (
                  <>
                    <div className="rounded-md border">
                      <div className="grid grid-cols-5 p-4 text-sm font-medium">
                        <div>Utilisateur</div>
                        <div>Formation</div>
                        <div>Date</div>
                        <div>Statut</div>
                        <div className="text-right">Actions</div>
                      </div>
                      <div className="divide-y">
                        {pendingInscriptions.map((inscription) => (
                          <div key={inscription.id} className="grid grid-cols-5 items-center p-4">
                            <div>{inscription.fullName}</div>
                            <div className="truncate" title={inscription.formation}>
                              {inscription.formation}
                            </div>
                            <div>{formatDate(inscription.demandeDate)}</div>
                            <div>
                              <Badge variant="outline" className={getStatusBadgeClass(inscription.status)}>
                                {inscription.status}
                              </Badge>
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => openUserDetails(inscription)}
                              >
                                Voir
                              </Button>
                              <Button 
                                size="sm"
                                onClick={() => handleApproveInscription(inscription.id)}
                              >
                                Approuver
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <a href="/admin/inscriptions">
                        <Button variant="outline">Voir toutes les inscriptions</Button>
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <CheckSquare className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune inscription en attente</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Toutes les inscriptions ont été traitées.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal de détails utilisateur */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Détails de l'inscription</DialogTitle>
            <DialogDescription>
              Informations complètes sur l'apprenant et sa demande d'inscription
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Informations personnelles</h3>
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Nom complet :</p>
                    <p className="text-sm">{selectedUser.name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Email :</p>
                    <p className="text-sm">{selectedUser.email}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Téléphone :</p>
                    <p className="text-sm">{selectedUser.phone}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Détails de l'inscription</h3>
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Formation :</p>
                    <p className="text-sm">{selectedUser.course}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Date de demande :</p>
                    <p className="text-sm">{formatDate(selectedUser.requestDate)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Statut :</p>
                    <Badge variant="outline" className={getStatusBadgeClass(selectedUser.status)}>
                      {selectedUser.status}
                    </Badge>
                  </div>
                  {selectedUser.approvalDate && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Date d'approbation :</p>
                      <p className="text-sm">{formatDate(selectedUser.approvalDate)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="sm:justify-start gap-2">
            {selectedUser && selectedUser.status === 'En attente' && (
              <Button 
                onClick={() => selectedUser && handleApproveInscription(selectedUser.id)}
                className="mr-auto"
              >
                Approuver cette inscription
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={() => setIsDetailsModalOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}